import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useState } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import urls from '@data/i18n/urls.json'

import useCart from '@context/useCartContext'
import { lang, searchUrl } from '@lib/constants'
import type { CartItem } from '@lib/types/cart'
import type { MenuData } from '@lib/types/menu'

import BagShoppingIcon from '@components/icons/bag-shopping'
import BarsIcon from '@components/icons/bars'
import MagnifyingGlassIcon from '@components/icons/magnifying-glass'
import PrismicLink from '@components/PrismicLink'

import Logo from './Logo'
import MobileMenuSlider from './MobileMenuSlider'

type MobileavParams = {
	menuData: MenuData
	isLoggedIn: boolean
	headerContents?: any
}

export default function MobileNav({ menuData, isLoggedIn, headerContents }: MobileavParams) {
	// Menu open state
	const [menuOpen, toggleMenuOpen] = useState(false)
	const [buttonAnimated, setButtonAnimated] = useState(false)
	const { toggleCart, items, itemCount } = useCart()

	const router = useRouter()
	const isOnSubscriptionPage = router.asPath.startsWith('/subscriptions/')

	// Handle close menu on route change
	useEffect(() => {
		const handleRouteChange = () => {
			toggleMenuOpen(false)
		}

		router.events.on('routeChangeStart', handleRouteChange)

		return () => {
			router.events.off('routeChangeStart', handleRouteChange)
		}
	})

	// Animate button when headerContents data loads
	useEffect(() => {
		if (headerContents?.cta_button_link && headerContents?.cta_button_text) {
			// Small delay for a polished entrance after data loads
			const timer = setTimeout(() => {
				setButtonAnimated(true)
			}, 50)

			return () => clearTimeout(timer)
		}
		// Reset animation state if data is not available
		setButtonAnimated(false)
		return undefined
	}, [headerContents])

	return (
		<nav aria-label="Mobile navigation" className="lg:hidden">
			<div className="container flex items-center justify-between px-1">
				{/* Logo - Left aligned */}
				<Link legacyBehavior href="/" prefetch={false}>
					<a className="inline-flex flex-shrink-0" title="Birchbox" aria-label="Birchbox Home">
						<Logo className="w-24 flex-shrink-0 fill-bbx-style" />
					</a>
				</Link>

				{/* All icons on the right */}
				<div className="flex items-center">
					{/* Hamburger menu */}
					<button
						className="px-3 py-6 text-lg"
						aria-label="Toggle navigation"
						type="button"
						onClick={() => toggleMenuOpen(true)}
					>
						<BarsIcon />
					</button>

					{/* <Link legacyBehavior href={searchUrl} prefetch={false}>
						<a className="relative px-4 py-6" aria-label="Search">
							<MagnifyingGlassIcon classes="w-[17px] fill-bbx-style" />
						</a>
					</Link> */}

					{/* Recursive menu slider */}
					<MobileMenuSlider
						slideIn={menuOpen}
						items={menuData?.items}
						level={1}
						closeMenu={() => toggleMenuOpen(false)}
						isLoggedIn={isLoggedIn}
						profileUrl={urls.subNav.profile.url}
					/>

					{/* Toggle cart button */}
					<button
						className={`flex items-center relative px-3 py-6 ${
							Array.isArray(items) && items?.length > 0 ? 'text-bbx-spark pr-1' : ''
						}`}
						type="button"
						aria-label="Cart"
						onClick={toggleCart}
					>
						<BagShoppingIcon classes="fill-bbx-style w-4" />

						{/* Items in cart count */}
						{Array.isArray(items) && items?.length > 0 && (
							<span className="w-6 ml-2 -mb-1 font-medium text-center rounded bg-bbx-pearl-light">
								{itemCount}
							</span>
						)}
					</button>
					<div className="ml-2 min-w-[120px] h-[32px] flex items-center">
						{headerContents?.cta_button_link && headerContents?.cta_button_text && (
							(isOnSubscriptionPage && lang === 'en-us') ? (
								// eslint-disable-next-line @next/next/no-html-link-for-pages
								<Link legacyBehavior href="/get-started">
									<a
										className={`button button-primary px-3 py-1.5 text-sm whitespace-nowrap transition-all duration-700 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
											buttonAnimated
												? 'opacity-100 scale-100'
												: 'opacity-0 scale-[0.96]'
										}`}
									>
										{headerContents.cta_button_text}
									</a>
								</Link>
							) : (
								<PrismicLink
									link={headerContents.cta_button_link}
									label={headerContents.cta_button_text}
									className={`button button-primary px-3 py-1.5 text-sm whitespace-nowrap transition-all duration-700 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
										buttonAnimated
											? 'opacity-100 scale-100'
											: 'opacity-0 scale-[0.96]'
									}`}
								/>
							)
						)}
					</div>
				</div>
			</div>
		</nav>
	)
}
